import { motion } from "framer-motion";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_MEMBER_DETAILS } from "../graphql/queries";
import { getProfileCompletionPercentage } from "../utils/profileCompletion";
import { CheckCircle2 } from "lucide-react";
import EditUserDialog from "./EditUserModal";
import TKSMemberCard from "./TKSMemberCard";
import NotLoggedIn from "../components/LoginExceptions";

const ProfilePage = () => {
    const navigate = useNavigate();
    const [mobile, setMobile] = useState("");
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const cardRef = useRef(null);
    const storedUserType = localStorage.getItem("user_type");
    const handleEdit = (user) => {
        setSelectedUser(user);
        setOpenEditModal(true);
    };
    useEffect(() => {
        const storedMobile = localStorage.getItem("mobile");
        if (storedMobile) {
            setMobile(storedMobile);
        }
    }, []);
    const { loading, error, data } = useQuery(GET_MEMBER_DETAILS, {
        variables: { mobile },
        skip: !mobile,
    });
    const member = data?.member;
    const redText = "text-red-600 font-medium";
    const fieldClass = "text-sm text-gray-600";
    if (!storedUserType) {
        return <NotLoggedIn />;
    }

    const renderField = (value) =>
        value && value.trim() !== ""
            ? value
            : <span className={redText}>Need to update</span>;

    if (loading)
        return (
            <div className="min-h-screen flex flex-col">
                <div className="flex-grow flex justify-center items-center">
                    <p className="text-gray-600">Loading...</p>
                </div>
                <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
                    © 2025 Your Organization. All rights reserved.
                </footer>
            </div>
        );

    if (error)
        return (
            <div className="min-h-screen flex flex-col">
                <div className="flex-grow flex justify-center items-center">
                    <p className="text-red-600">Error loading member details.</p>
                </div>
                <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
                    © 2025 Your Organization. All rights reserved.
                </footer>
            </div>
        );

    if (!member)
        return (
            <div className="min-h-screen flex flex-col">
                <div className="flex-grow flex justify-center items-center">
                    <p className="text-gray-600">No member data available.</p>
                </div>
                <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
                    © 2025 Your Organization. All rights reserved.
                </footer>
            </div>
        );
    const completion = getProfileCompletionPercentage(member);
    const isPaid = member.Payment_Status === "Paid";

    return (
        <div style={{ backgroundColor: '#f8f9e9' }} className="min-h-screen flex flex-col items-center">
            {/* Yellow Banner */}
            <div className="w-full bg-yellow-200 relative pt-16 pb-36 overflow-hidden">
                {/* Decorative Circles */}
                <div className="absolute inset-0">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-300 rounded-full opacity-30"></div>

                    <div className="absolute top-32 right-10 w-24 h-24 bg-yellow-300 rounded-full opacity-30"></div>
                </div>

                {/* Top-right Buttons */}
                <div className="absolute top-3 right-4 flex gap-3 z-10">
                    {completion < 100 && !isPaid && (
                        <>
                            <div className="flex flex-col items-center space-y-2">
                                <p className="text-red-600 font-medium text-center">
                                    Complete your profile to obtain MEMBERSHIP ID
                                </p>
                            </div>
                        </>

                    )}
                    {completion === 100 && !isPaid && (
                        <>
                            <p className="text-green-700 font-medium">
                                You're almost there! Pay ₹2000 to obtain your MEMBERSHIP ID
                            </p>
                        </>
                    )}
                    {completion === 100 && isPaid && (
                        <>
                            <span className="bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded shadow inline-flex items-center gap-1">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                {member.id}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Profile Card */}
            <div className="relative w-full max-w-4xl px-4 -mt-32 z-20 top-10 ">
                {/* Avatar */}
                <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 z-30">
                    <div className="bg-gradient-to-br from-yellow-300 to-yellow-500 h-24 w-24 rounded-full flex items-center justify-center text-3xl font-bold text-white">
                        {member.First_Name?.[0] || "?"}
                        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }} ref={cardRef}>
                            <TKSMemberCard member={member} />
                        </div>
                    </div>
                </div>

                <div style={{ backgroundColor: '#F8F9E9' }} className="rounded-2xl shadow-lg pt-16 pb-10 px-6 sm:px-10 text-center">
                    {/* Name and DOB */}
                    <h2 className="text-xl font-semibold mb-1">{renderField(member.First_Name)} {renderField(member.Last_Name)}</h2>
                    <p className={fieldClass}>{renderField(member.Mobile_Number)}</p>
                    {/* Profile Status Stepper */}
                    {(completion < 100 || !isPaid) && (
                        <>
                            <div className="flex justify-center mt-6">
                                <div className="flex items-center space-x-3 sm:space-x-6">
                                    {/* Step 1: Registered */}
                                    <div className="flex flex-col items-center">
                                        <div className="w-8 h-8 flex items-center justify-center rounded-full text-white font-semibold shadow bg-green-500">
                                            <CheckCircle2 size={18} />
                                        </div>
                                        <span className="text-xs mt-1 text-gray-700">Registered</span>
                                    </div>

                                    {/* Connector */}
                                    <div className="w-10 h-1 bg-gray-300 rounded-full"></div>

                                    {/* Step 2: Profile */}
                                    <div className="flex flex-col items-center">
                                        <div className={`w-8 h-8 flex items-center justify-center rounded-full font-semibold shadow ${completion === 100 ? "bg-green-500 text-white" : "bg-yellow-400 text-white"}`}>
                                            {completion === 100 ? <CheckCircle2 size={18} /> : "!"}
                                        </div>
                                        <span className="text-xs mt-1 text-gray-700">Profile</span>
                                    </div>

                                    {/* Connector */}
                                    <div className="w-10 h-1 bg-gray-300 rounded-full"></div>

                                    {/* Step 3: Payment */}
                                    <div className="flex flex-col items-center">
                                        <div className={`w-8 h-8 flex items-center justify-center rounded-full font-semibold shadow ${isPaid ? "bg-green-500 text-white" : "bg-gray-300 text-gray-700"}`}>
                                            {isPaid ? <CheckCircle2 size={18} /> : "3"}
                                        </div>
                                        <span className="text-xs mt-1 text-gray-700">Payment</span>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6" />
                        </>
                    )}
                    <div className="flex justify-center">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-sm relative max-w-3xl w-full">

                            {/* Left column */}
                            <div className="space-y-4 pl-4">
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Date of Birth:</span>
                                    <span className="text-right w-1/2">{renderField(member.DOB)}</span>
                                </div>
                                <div className="flex flex-wrap justify-between">
                                    <span className="font-bold text-left w-1/2 break-words">E-Mail:</span>
                                    <span className="text-right w-1/2 break-words">{renderField(member.Email)}</span>
                                </div>
                                <div className="flex flex-wrap justify-between">
                                    <span className="font-bold text-left w-1/2 break-words">Occupation:</span>
                                    <span className="text-right w-1/2 break-words">{renderField(member.Occupation)}</span>
                                </div>
                                <div className="flex flex-wrap justify-between">
                                    <span className="font-bold text-left w-1/2 break-words">Address:</span>
                                    <span className="text-right w-1/2 break-words">{renderField(member.Address)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">City:</span>
                                    <span className="text-gray-900">{renderField(member.City)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">District:</span>
                                    <span className="text-gray-900">{renderField(member.District)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">State:</span>
                                    <span className="text-gray-900">{renderField(member.State)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Pincode:</span>
                                    <span className="text-gray-900">{renderField(member.Pincode)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Marital Status:</span>
                                    <span className="text-gray-900">{renderField(member.Marital_Status)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Family Details:</span>
                                    <span className="text-right w-1/2">{renderField(member.Family_Details)}</span>
                                </div>
                            </div>

                            {/* Right column */}
                            <div className="space-y-4 pl-4">
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Member ID:</span>
                                    <span className="text-right w-1/2">{renderField(member.id)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Payment Status:</span>
                                    <span
                                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${member.Payment_Status === "Paid"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"}`}
                                    >
                                        {member.Payment_Status || "Not Paid"}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">ID Proof Type:</span>
                                    <span className="text-right w-1/2">{renderField(member.ID_Proof_Type)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">ID Proof Number:</span>
                                    <span className="text-right w-1/2">{renderField(member.ID_Proof_Number)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">PAN Card:</span>
                                    <span className="text-right w-1/2">{renderField(member.Pan_Card)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">PAN Number:</span>
                                    <span className="text-right w-1/2">{renderField(member.Pan_Number)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Star:</span>
                                    <span className="text-gray-900">{renderField(member.Star)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Raasi:</span>
                                    <span className="text-gray-900 text-right">{renderField(member.Raasi)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Gothram :</span>
                                    <span className="text-gray-900">{renderField(member.Kootram)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-left w-1/2">Kuladeivam:</span>
                                    <span className="text-gray-900">{renderField(member.Kuladeivam)}</span>
                                </div>
                            </div>

                            {/* Vertical divider */}
                            <div className="hidden sm:block absolute left-1/2 top-0 h-full w-px bg-gray-300"></div>
                        </div>
                    </div>
                    {/* Address & Contact */}
                    <div className="mt-8 text-sm text-gray-800 space-y-1">

                        <div className="flex items-center justify-center gap-2 mt-1">
                            {completion < 100 && !isPaid && (
                                <>
                                    <div className="flex items-center justify-center space-x-4">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => { navigate("/completemyprofile"); window.scrollTo(0, 0); }}
                                            className="bg-yellow-500 text-white px-6 py-2 rounded-full font-semibold shadow"
                                        >
                                            Complete My Profile
                                        </motion.button>

                                        <span className="text-gray-600 font-medium">or</span>

                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => {
                                                window.scrollTo(0, 0); // Scroll to top
                                                navigate("/paymentgateway");
                                            }}
                                            className="bg-green-600 text-white px-6 py-2 rounded-full font-semibold shadow"
                                        >
                                            Proceed to Payment
                                        </motion.button>
                                    </div>
                                </>

                            )}
                            {completion === 100 && !isPaid && (
                                <>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => { navigate("/paymentgateway"); window.scrollTo(0, 0); }}
                                        className="bg-green-600 text-white px-6 py-2 rounded-full font-semibold shadow"
                                    >
                                        Proceed to Payment
                                    </motion.button>
                                </>
                            )}
                            {isPaid && (
                                <div className="relative">
                                    <div className="flex items-center space-x-4 mr-15">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => handleEdit(member)}
                                            className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold shadow"
                                            style={{ backgroundColor: '#2A289C' }}
                                        >
                                            Edit Profile
                                        </motion.button>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: '#f8f9e9' }} className="w-full py-10" />
            <EditUserDialog
                isOpen={openEditModal}
                onClose={() => setOpenEditModal(false)}
                user={selectedUser}
            />


        </div>
    );
};
export default ProfilePage;